/**
 * DocReviewActions — approve / request changes / reject with confirm flow.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { reviewHubUpload } from '../../../api/projects'
import type { HubInboxItem, HubRejectionType, HubReviewAction } from '../../../api/types'
import { parseMoneyInput } from '../../../lib/formatters'
import MoneyInput from '../../../components/MoneyInput'

type Step = 'idle' | 'confirming' | 'submitting' | 'done'

const REJECTION_OPTIONS: { value: HubRejectionType; label: string }[] = [
  { value: 'missing_retention',   label: 'Missing retention' },
  { value: 'missing_lien_waiver', label: 'Missing lien waiver' },
  { value: 'insurance_expired',   label: 'Insurance expired' },
  { value: 'format_error',        label: 'Format error' },
  { value: 'other',               label: 'Other' },
]

const DONE_LABEL: Record<HubReviewAction, string> = {
  approved:           'Approved',
  changes_requested:  'Changes requested',
  rejected:           'Rejected',
  paid:               'Marked as paid',
}

interface Props {
  projectId:         number
  uploadId:          number
  item:              HubInboxItem
  onReviewComplete?: (updated: HubInboxItem) => void
}

export default function DocReviewActions({ projectId, uploadId, item, onReviewComplete }: Props) {
  const queryClient = useQueryClient()
  const [step, setStep]               = useState<Step>('idle')
  const [action, setAction]           = useState<HubReviewAction | null>(null)
  const [comment, setComment]         = useState('')
  const [rejectionType, setRejectionType] = useState<HubRejectionType>('other')
  const [error, setError]             = useState<string | null>(null)
  const [doneAction, setDoneAction]   = useState<HubReviewAction | null>(null)
  const [amountStr, setAmountStr]     = useState<string>(item.amount ?? '')
  const [payMethod, setPayMethod]     = useState<string>('Check')
  const [payReference, setPayReference] = useState<string>('')

  const isInvoice = item.doc_type === 'invoice'
  const amountNum = parseMoneyInput(amountStr)
  const validAmount = Number.isFinite(amountNum) && amountNum > 0

  const terminal = item.status === 'approved' || item.status === 'rejected' || item.status === 'changes_requested'

  if (terminal && step !== 'done' && !(isInvoice && item.status === 'approved' && step === 'confirming' && action === 'paid')) {
    const paidAmt = item.paid_amount != null ? parseFloat(item.paid_amount) : null
    return (
      <div className="pm-dr-aside-panel">
        <div className="pm-dr-aside-h">Review complete</div>
        <div style={{ padding: '11px 12px', fontSize: 12, color: 'var(--ink3)' }}>
          {item.status === 'approved' && !item.paid_at && 'This document has been approved.'}
          {item.status === 'approved' && item.paid_at && (
            <span style={{ color: 'var(--green)' }}>
              Paid{paidAmt != null ? ` $${paidAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''} on {new Date(item.paid_at).toLocaleDateString()}.
            </span>
          )}
          {item.status === 'rejected' && 'This document has been rejected.'}
          {item.status === 'changes_requested' && 'Changes requested — waiting on sub resubmission.'}
        </div>
        {isInvoice && item.status === 'approved' && !item.paid_at && (
          <div style={{ padding: '0 12px 11px' }}>
            <button type="button" className="btn b-dk" style={{ width: '100%', justifyContent: 'center', padding: 8, fontWeight: 600 }}
              onClick={() => pick('paid')} data-testid="dr-mark-paid">
              Mark as paid
            </button>
          </div>
        )}
      </div>
    )
  }

  if (step === 'done' && doneAction) {
    return (
      <div className="pm-dr-aside-panel">
        <div className="pm-dr-aside-h">{DONE_LABEL[doneAction]}</div>
        <div style={{ padding: '11px 12px', fontSize: 12, color: 'var(--green)' }}>
          {DONE_LABEL[doneAction]} successfully.
        </div>
      </div>
    )
  }

  async function submit() {
    if (!action) return
    if (action !== 'approved' && action !== 'paid' && !comment.trim()) {
      setError('Comment is required.')
      return
    }
    if (action === 'paid' && !validAmount) {
      setError('Enter the amount you paid.')
      return
    }

    setStep('submitting')
    setError(null)

    try {
      const res = await reviewHubUpload(projectId, uploadId, {
        action,
        comment: action === 'approved' || action === 'paid' ? undefined : comment.trim(),
        rejection_type: action === 'rejected' ? rejectionType : undefined,
        amount: (action === 'approved' || action === 'paid') && isInvoice && validAmount
          ? Math.round(amountNum * 100) / 100
          : undefined,
        payment_method: action === 'paid' ? payMethod : undefined,
        payment_reference: action === 'paid' && payReference.trim() ? payReference.trim() : undefined,
      })
      const updated = { ...item, ...res.data, status: res.data.status }
      setDoneAction(action)
      setStep('done')
      onReviewComplete?.(updated)
      queryClient.invalidateQueries({ queryKey: ['project-inbox', projectId] })
    } catch (err) {
      setStep('confirming')
      setError(err instanceof Error ? err.message : 'Review failed')
    }
  }

  function pick(next: HubReviewAction) {
    setAction(next)
    setComment('')
    setRejectionType('other')
    setError(null)
    setStep('confirming')
  }

  if (step === 'idle') {
    return (
      <div className="pm-dr-actions">
        <button type="button" className="btn b-grn" style={{ justifyContent: 'center', padding: 9, fontWeight: 600 }} onClick={() => pick('approved')}>
          ✓ Approve
        </button>
        <button type="button" className="btn b-ol" style={{ justifyContent: 'center', padding: 8 }} onClick={() => pick('changes_requested')}>
          Request Changes
        </button>
        <button type="button" className="btn b-ol" style={{ justifyContent: 'center', padding: 8, color: 'var(--red)', borderColor: 'var(--red-l)' }} onClick={() => pick('rejected')}>
          ✕ Reject
        </button>
      </div>
    )
  }

  return (
    <div className="pm-dr-aside-panel">
      <div className="pm-dr-aside-h">
        {action === 'approved' ? 'Confirm approval' : action === 'rejected' ? 'Confirm rejection' : action === 'paid' ? 'Mark as paid' : 'Request changes'}
      </div>
      <div style={{ padding: '8px 12px' }}>
        {isInvoice && (action === 'approved' || action === 'paid') && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>
              {action === 'paid' ? 'Amount paid' : 'Invoice amount (optional)'}
            </div>
            <MoneyInput
              className="fld-i mono"
              placeholder="0.00"
              value={amountStr}
              onChange={setAmountStr}
              disabled={step === 'submitting'}
              style={{ width: '100%', padding: '6px 10px' }}
              data-testid="dr-amount"
            />
          </div>
        )}
        {action === 'paid' && (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>
                Payment method (optional)
              </div>
              <select
                className="fld-i"
                value={payMethod}
                onChange={e => setPayMethod(e.target.value)}
                disabled={step === 'submitting'}
                style={{ width: '100%', padding: '6px 10px' }}
                data-testid="dr-pay-method"
              >
                <option value="Check">Check</option>
                <option value="ACH">ACH</option>
                <option value="Wire">Wire</option>
                <option value="Zelle">Zelle</option>
                <option value="Card">Card</option>
              </select>
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>
                Reference # (optional — check #, wire ref, etc.)
              </div>
              <input
                className="fld-i"
                placeholder="e.g. 12345"
                value={payReference}
                onChange={e => setPayReference(e.target.value)}
                disabled={step === 'submitting'}
                style={{ width: '100%', padding: '6px 10px' }}
                data-testid="dr-pay-reference"
              />
            </div>
          </>
        )}
        {action === 'rejected' && (
          <select
            className="fld-i"
            style={{ width: '100%', padding: '6px 10px', marginBottom: 8 }}
            value={rejectionType}
            onChange={e => setRejectionType(e.target.value as HubRejectionType)}
            disabled={step === 'submitting'}
          >
            {REJECTION_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        {action !== 'approved' && action !== 'paid' && (
          <textarea
            placeholder={action === 'rejected' ? 'Rejection reason (required)…' : 'What needs to change? (required)…'}
            value={comment}
            onChange={e => setComment(e.target.value)}
            disabled={step === 'submitting'}
            style={{
              width: '100%', padding: '7px 10px',
              border: '1px solid var(--line)', borderRadius: 'var(--rs)',
              fontSize: 11, fontFamily: 'inherit', color: 'var(--ink)',
              background: 'var(--bg)', resize: 'vertical', minHeight: 54,
            }}
          />
        )}
        {error && (
          <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 8 }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            type="button"
            className="btn b-grn"
            style={{ flex: 1, justifyContent: 'center', padding: 8, fontWeight: 600 }}
            disabled={step === 'submitting'
              || (action !== 'approved' && action !== 'paid' && !comment.trim())
              || (action === 'paid' && !validAmount)}
            onClick={submit}
          >
            {step === 'submitting' ? 'Submitting…' : 'Confirm'}
          </button>
          <button
            type="button"
            className="btn b-ol"
            style={{ padding: '8px 14px' }}
            disabled={step === 'submitting'}
            onClick={() => { setStep('idle'); setAction(null); setError(null) }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
